import apiClient from '../apiClient';
import type { ApiResponse, MachineConfig, MachineConfigData } from '@/types';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactQueryKeys } from '../reactQueryKeys';

const ADMIN_URL = '/admin/machines';

export const getMachineConfig = async (machineId: string): Promise<MachineConfig> => {
  const { data } = await apiClient.get<ApiResponse<MachineConfig>>(`${ADMIN_URL}/${machineId}/config`);
  return data.data;
};

export const updateMachineConfig = async (
  machineId: string,
  config: Partial<MachineConfigData>
): Promise<MachineConfig> => {
  const { data } = await apiClient.put<ApiResponse<MachineConfig>>(
    `${ADMIN_URL}/${machineId}/config`,
    config
  );
  return data.data;
};

export const pushMachineConfig = async (machineId: string): Promise<void> => {
  await apiClient.post(`${ADMIN_URL}/${machineId}/config/push`);
};

export const pullMachineConfig = async (machineId: string): Promise<void> => {
  await apiClient.post(`${ADMIN_URL}/${machineId}/config/pull`);
};

export const requestDeviceStatus = async (machineId: string): Promise<void> => {
  await apiClient.post(`${ADMIN_URL}/${machineId}/status/request`);
};

export const syncProducts = async (machineId: string): Promise<void> => {
  await apiClient.post(`${ADMIN_URL}/${machineId}/products/sync`);
};

// React Query Hooks

export const useGetMachineConfig = (machineId: string) =>
  useQuery({
    queryKey: [ReactQueryKeys.GET_MACHINE_CONFIG, machineId],
    queryFn: () => getMachineConfig(machineId),
    enabled: !!machineId,
  });

export const useUpdateMachineConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ machineId, config }: { machineId: string; config: Partial<MachineConfigData> }) =>
      updateMachineConfig(machineId, config),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [ReactQueryKeys.GET_MACHINE_CONFIG, variables.machineId],
      });
    },
  });
};

export const usePushMachineConfig = () =>
  useMutation({
    mutationFn: (machineId: string) => pushMachineConfig(machineId),
  });

export const usePullMachineConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (machineId: string) => pullMachineConfig(machineId),
    onSuccess: (_data, machineId) => {
      // Poll for config response from device (arrives via MQTT → handler → DB)
      const delays = [2000, 4000, 7000];
      delays.forEach((delay) => {
        setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: [ReactQueryKeys.GET_MACHINE_CONFIG, machineId],
          });
        }, delay);
      });
    },
  });
};

export const useRequestDeviceStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (machineId: string) => requestDeviceStatus(machineId),
    onSuccess: (_data, machineId) => {
      const delays = [2000, 4000, 7000];
      delays.forEach((delay) => {
        setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: [ReactQueryKeys.GET_MACHINE_CONFIG, machineId],
          });
        }, delay);
      });
    },
  });
};

export const useSyncProducts = () =>
  useMutation({
    mutationFn: (machineId: string) => syncProducts(machineId),
  });
